import { useHistory, useLocation } from 'react-router'
import { useFormikContext } from 'formik'
import { Box, Text, Stack, useResponsiveValue, Inline } from '@machobear/design-system'

import { useTranslation } from 'react-i18next'

import client from '@/ports/api-client'
import Skeleton from '@/components/skeleton'
import { CouponField, CustomTag } from '@/components/coupon-field'
import formatCurrency from '@/utils/format-currency'
import { useGetProduct } from '@/hooks/use-block-and-pay'
import { useFormContext } from '../form-context'

type Props = {
  state: string
  couponCode: string
}

type SubmitValues = {
  couponCode: string
}

const PaymentSummary = ({ state, couponCode }: Props) => {
  const { t } = useTranslation()
  const responsiveValue = useResponsiveValue()
  const history = useHistory()
  const location = useLocation()
  const isMobile = responsiveValue({
    mobile: true,
    tablet: false
  })
  const { values, setSubmitting, setFieldError, isSubmitting } = useFormikContext<SubmitValues>()
  const { setCouponCode } = useFormContext()

  const { data, isLoading } = useGetProduct('credit-check-single-package', {
    couponCode,
    state
  })

  const product = data?.products[0]

  const onCouponSubmit = async () => {
    setSubmitting(true)

    try {
      await client.get(
        `/catalogs/${state}?couponCode=${values.couponCode}&plan=${product.plan_name}`,
        {
          noLoading: true
        }
      )

      setSubmitting(false)
      setCouponCode(values.couponCode)

      return history.replace(`${location.pathname}?couponCode=${values.couponCode}`)
    } catch (error) {
      setSubmitting(false)
      setFieldError(
        'couponCode',
        t('l.promotions.coupon.invalid.text', 'Invalid or expired coupon code')
      )
    }
  }

  const handleDeleteCoupon = () => {
    history.replace(`${location.pathname}`)
    setCouponCode('')
  }

  return (
    <Box width="full">
      <Stack space={{ mobile: 'large', desktop: 'xxlarge' }}>
        <Box>
          <Box paddingBottom="medium" borderBottom="borderNeutralLight">
            <Stack space="medium">
              <Box display="flex" justifyContent="spaceBetween">
                {isLoading ? (
                  <Skeleton width="329" height="24" />
                ) : (
                  <Box display="flex" justifyContent="flexStart">
                    <Text size="medium" weight="regular" tone="secondary">
                      {product?.pack_quantity} x
                    </Text>
                    <Box
                      display="flex"
                      flexDirection="column"
                      justifyContent="flexStart"
                      marginLeft="xxxsmall"
                      gap="xsmall"
                      style={{ maxWidth: isMobile ? '184px' : '232px' }}
                    >
                      <Text size="medium" weight="regular" tone="secondary">
                        {product?.name}
                      </Text>
                    </Box>
                  </Box>
                )}

                {isLoading ? (
                  <Skeleton width="80" height="24" />
                ) : (
                  <Inline space="xxxsmall" alignY="center" align="right">
                    <Text size="medium" weight="medium">
                      {formatCurrency(product?.value)}
                    </Text>
                  </Inline>
                )}
              </Box>
            </Stack>
          </Box>

          <Box paddingY="medium" borderBottom="borderField">
            <Stack space="small">
              <Box display="flex" justifyContent="spaceBetween">
                <Text size="medium" weight="regular">
                  {t('r.payment.edit.subtotal.label', 'Subtotal')}
                </Text>
                <Text size="medium" weight="medium">
                  {isLoading ? (
                    <Skeleton width="80" height="24" />
                  ) : (
                    `CA${formatCurrency(
                      product?.total_excluding_discount ??
                        product?.regular_price ??
                        product?.total_before_tax ??
                        product?.total_excluding_tax
                    )}`
                  )}
                </Text>
              </Box>
              <Box>
                {couponCode ? (
                  <Box display="flex" justifyContent="spaceBetween" alignItems="center">
                    <Inline space="xxsmall" alignY="center">
                      <Text size="small" weight="regular">
                        {t('r.payment.edit.discount.label', 'Discount')}
                      </Text>
                      {isLoading ? (
                        <Skeleton width="80" height="24" />
                      ) : (
                        <CustomTag name={couponCode} onClear={handleDeleteCoupon} />
                      )}
                    </Inline>
                    <Text size="small" weight="medium">
                      {isLoading ? (
                        <Skeleton width="80" height="24" />
                      ) : (
                        `-${formatCurrency(
                          product?.discount_from_regular_price ?? product?.discount
                        )}`
                      )}
                    </Text>
                  </Box>
                ) : (
                  <CouponField
                    couponCode={couponCode}
                    onApply={onCouponSubmit}
                    isSubmitting={isSubmitting}
                  />
                )}
              </Box>
              <Box display="flex" justifyContent="spaceBetween">
                <Text size="xsmall" weight="regular" tone="tertiary">
                  GST/HST
                </Text>
                <Text size="xsmall" weight="regular" tone="tertiary">
                  {isLoading ? (
                    <Skeleton width="80" height="24" />
                  ) : state ? (
                    formatCurrency(product?.total_tax)
                  ) : (
                    '-'
                  )}
                </Text>
              </Box>
            </Stack>
          </Box>
          <Box display="flex" justifyContent="spaceBetween" marginTop="medium">
            <Text size="large" weight="strong">
              {t('l.promotions.total.text', 'Total')}
            </Text>
            <Text size="large" weight="strong">
              {isLoading ? (
                <Skeleton width="200" height="24" />
              ) : (
                `CA${formatCurrency(
                  state
                    ? (product?.total_due ?? product?.total)
                    : (product?.total_before_tax ?? product?.total)
                )}`
              )}
            </Text>
          </Box>
        </Box>
      </Stack>
    </Box>
  )
}

export default PaymentSummary
